import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, ShieldCheck, Loader2, AlertCircle, Eye, EyeOff } from 'lucide-react';

// ✅ ENV based API
const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001';

const AdminLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Forgot Password States
  const [showForgot, setShowForgot] = useState(false);
  const [step, setStep] = useState(1); // 1: Email, 2: OTP
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) return;

    setLoading(true);
    setError('');

    try {
      const res = await fetch(`${API_BASE}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      const data = await res.json();

      if (res.ok && data.success) {
        // ✅ Save JWT
        localStorage.setItem('adminToken', data.token);

        // ✅ REDIRECT TO DASHBOARD (FIX)
        navigate('/admin');
      } else {
        setError(data.message || 'Invalid credentials');
      }
    } catch (err) {
      console.error('Login Error:', err);
      setError('Unable to connect to server');
    } finally {
      setLoading(false);
    }
  };

  // 1. Send OTP
  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (!email) {
      setError("Please enter your email.");
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/admin/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setStep(2);
        setSuccessMsg(data.message);
      } else {
        setError(data.message || "Failed to send OTP");
      }
    } catch (e) {
      setError("Server connection failed");
    } finally {
      setLoading(false);
    }
  };

  // 2. Verify OTP & Reset
  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!otp || !newPassword) {
      setError("Please fill all fields.");
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/admin/reset-password-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, otp, newPassword }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        // alert("✅ Password Reset Successful! Please login."); // Removed alert
        setShowForgot(false);
        setStep(1);
        setOtp('');
        setNewPassword('');
        setSuccessMsg("✅ Password Reset Successful! Please login."); // Show message in login form
      } else {
        setError(data.message || "Reset failed");
      }
    } catch (e) {
      setError("Server connection failed");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = () => {
    setShowForgot(true);
    setError('');
    setSuccessMsg('');
    setStep(1);
  };

  return (
    <div className="flex flex-col items-center justify-center py-20 px-4 bg-gray-50 min-h-[70vh]">
      <div className="w-full max-w-md bg-white shadow-xl rounded-2xl overflow-hidden border border-gray-100">
        {/* HEADER */}
        <div className="bg-white p-8 pb-0 text-center">
          <div className="inline-flex p-3 bg-purple-50 rounded-full text-exiser-primary mb-4 shadow-sm">
            <ShieldCheck className="w-8 h-8" />
          </div>
          <h1 className="text-2xl font-bold uppercase text-gray-900 tracking-wide">
            Admin Portal
          </h1>
          <p className="text-gray-500 text-sm mt-2 font-sans">
            Please enter your credentials to access the dashboard.
          </p>
        </div>

        {/* FORM */}
        {showForgot ? (
          /* --- FORGOT PASSWORD FLOW --- */
          <div className="p-8 space-y-6">
            <div className="text-center mb-4">
              <h2 className="text-xl font-bold text-gray-800">Reset Password</h2>
              <p className="text-sm text-gray-500">
                {step === 1 ? "Enter email to receive OTP" : "Enter OTP and New Password"}
              </p>
            </div>

            {error && (
              <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm flex items-center gap-2 border border-red-100">
                <AlertCircle className="w-4 h-4" /> {error}
              </div>
            )}
            {successMsg && (
              <div className="bg-green-50 text-green-600 p-3 rounded-lg text-sm border border-green-100">
                {successMsg}
              </div>
            )}

            {step === 1 ? (
              <form onSubmit={handleSendOtp} className="space-y-4">
                <div>
                  <label className="text-xs font-bold uppercase text-gray-600 ml-1">Email Address</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="block w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:ring-1 focus:ring-exiser-primary"
                    placeholder=""
                    required
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 bg-exiser-primary text-white font-bold uppercase rounded-lg hover:bg-purple-900 transition-all disabled:opacity-70"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "Send OTP"}
                </button>
              </form>
            ) : (
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div>
                  <label className="text-xs font-bold uppercase text-gray-600 ml-1">Enter OTP</label>
                  <input
                    type="text"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    className="block w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:ring-1 focus:ring-exiser-primary tracking-widest text-center font-mono text-lg"
                    placeholder="######"
                    maxLength={6}
                    required
                  />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase text-gray-600 ml-1">New Password</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="block w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:ring-1 focus:ring-exiser-primary"
                    placeholder=""
                    required
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 bg-green-600 text-white font-bold uppercase rounded-lg hover:bg-green-700 transition-all disabled:opacity-70"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "Reset Password"}
                </button>
              </form>
            )}

            <button
              onClick={() => { setShowForgot(false); setStep(1); setError(''); setSuccessMsg(''); }}
              className="w-full text-sm text-gray-500 hover:text-gray-800 underline mt-2"
            >
              Back to Login
            </button>
          </div>
        ) : (
          /* --- LOGIN FORM --- */
          <form onSubmit={handleLogin} className="p-8 space-y-6">
            {error && (
              <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm flex items-center gap-2 border border-red-100">
                <AlertCircle className="w-4 h-4" />
                {error}
              </div>
            )}

            {successMsg && (
              <div className="bg-green-50 text-green-600 p-3 rounded-lg text-sm flex items-center gap-2 border border-green-100">
                <ShieldCheck className="w-4 h-4" />
                {successMsg}
              </div>
            )}

            <div className="space-y-2">
              <label className="text-xs font-bold uppercase text-gray-600 tracking-wider ml-1">
                Email Address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="block w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:border-exiser-primary focus:ring-1 focus:ring-exiser-primary transition-all"
                placeholder=""
                required
              />
            </div>

            <div className="space-y-2">
              <label className="text-xs font-bold uppercase text-gray-600 tracking-wider ml-1">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="block w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:border-exiser-primary focus:ring-1 focus:ring-exiser-primary transition-all pr-12"
                  placeholder=""
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors focus:outline-none"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 py-4 bg-exiser-primary text-white font-bold uppercase tracking-wider rounded-lg hover:bg-purple-900 transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5 disabled:opacity-70 disabled:cursor-wait"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  Access Dashboard <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>

            <div className="text-center">
              <button
                type="button"
                onClick={handleForgotPassword}
                className="text-xs text-gray-500 hover:text-exiser-primary underline transition-colors"
              >
                Forgot Password?
              </button>
            </div>
          </form>
        )}

        {/* FOOTER */}
        <div className="bg-gray-50 px-8 py-4 border-t border-gray-100 text-center">
          <p className="text-xs text-gray-400">
            Restricted Access. Authorized Personnel Only.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;